import { CreateParams, DeleteParams, UpdateParams } from 'react-admin'
import { axiosDZAuthInstance } from '../axios'
import { DASHBOARD } from '../routes'
import { formatErrorMessage } from '../helpers'
import { EResource } from '@/admin/types'
import type {
  TVariantSuppliersResponse,
  TSupplierVariantsResponse,
  TSupplierMappingDetail,
} from '@/modules/SupplierMappings/types'

/** Parse composite id "variantId-supplierCode" into [variantId, supplierCode] */
export const parseCompositeId = (id: string): [string, string] => {
  const parts = String(id).split('-')
  if (parts.length < 2) {
    throw new Error(`Invalid supplier mapping id: ${id}. Expected format: variantId-supplierCode`)
  }
  const supplierCode = parts.slice(1).join('-')
  return [parts[0], supplierCode]
}

interface SupplierMappingCreatePayload {
  variantId: number
  supplierCode: string
  cost?: number
  priority: number
  isActive: boolean
  supplierMetaData?: Record<string, unknown>
}

const trimmer = (_key: string, value: unknown) => {
  if (typeof value === 'string') return value.trim()
  return value
}

/**
 * Create a supplier mapping. The API returns no id, so we derive it as variantId-supplierCode
 * to match the list format and satisfy react-admin's redirect requirement.
 */
export const createSupplierMapping = async (
  params: CreateParams,
): Promise<{ data: Record<string, unknown> & { id: string } }> => {
  const { supplierMetaData, ...rest } = params.data as Record<string, unknown>
  const payload: SupplierMappingCreatePayload = {
    ...rest,
    variantId: rest.variantId as number,
    supplierCode: rest.supplierCode as string,
    priority: rest.priority as number,
    isActive: rest.isActive as boolean,
  }
  if (rest.cost !== undefined && rest.cost !== null && rest.cost !== '') {
    payload.cost = rest.cost as number
  }

  if (supplierMetaData && typeof supplierMetaData === 'object' && !Array.isArray(supplierMetaData)) {
    payload.supplierMetaData = supplierMetaData as Record<string, unknown>
  }

  const trimmedPayload = JSON.parse(JSON.stringify(payload, trimmer))

  try {
    const { data } = await axiosDZAuthInstance.post(`${DASHBOARD()}/${EResource.SupplierMappings}`, trimmedPayload)

    const id = data.id ?? `${data.variantId}-${data.supplierCode}`

    return { data: { ...data, id } }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to create supplier mapping'
    throw new Error(message)
  }
}

/**
 * Delete a supplier mapping. Uses composite id (variantId-supplierCode) to call
 * DELETE /dashboard/supplier-mappings/{variantId}/{supplierCode}
 */
export const deleteSupplierMapping = async (params: DeleteParams): Promise<{ data: { id: string } }> => {
  const [variantId, supplierCode] = parseCompositeId(String(params.id))

  try {
    await axiosDZAuthInstance.delete(`${DASHBOARD()}/${EResource.SupplierMappings}/${variantId}/${supplierCode}`)
    return { data: { id: params.id as string } }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to delete supplier mapping'
    throw new Error(message)
  }
}

/**
 * GET /dashboard/supplier-mappings/{variantId}/{supplierCode}
 * Fetch a single supplier mapping
 */
export const getMapping = async (variantId: string, supplierCode: string): Promise<TSupplierMappingDetail> => {
  const { data } = await axiosDZAuthInstance.get<TSupplierMappingDetail>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/${variantId}/${encodeURIComponent(supplierCode)}`,
  )
  return data
}

/**
 * PUT /dashboard/supplier-mappings/{variantId}/{supplierCode}
 * Update a supplier mapping
 */
export const updateMapping = async (
  params: UpdateParams,
): Promise<{ data: Record<string, unknown> & { id: string } }> => {
  const [variantId, supplierCode] = parseCompositeId(String(params.id))
  const { cost, priority, isActive, supplierMetaData } = params.data as Record<string, unknown>

  const payload: Record<string, unknown> = {
    priority: priority as number,
    isActive: isActive as boolean,
  }
  if (cost !== undefined && cost !== null && cost !== '') {
    payload.cost = cost as number
  }
  if (supplierMetaData && typeof supplierMetaData === 'object' && !Array.isArray(supplierMetaData)) {
    payload.supplierMetaData = supplierMetaData as Record<string, unknown>
  }

  const trimmedPayload = JSON.parse(JSON.stringify(payload, trimmer))

  try {
    await axiosDZAuthInstance.put(
      `${DASHBOARD()}/${EResource.SupplierMappings}/${variantId}/${encodeURIComponent(supplierCode)}`,
      trimmedPayload,
    )
    return { data: { ...params.data, id: params.id as string } }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const rawMessage = err.response?.data?.message
    const message = rawMessage
      ? formatErrorMessage(rawMessage as Parameters<typeof formatErrorMessage>[0])
      : 'Failed to update supplier mapping'
    throw new Error(message)
  }
}

/**
 * GET /dashboard/supplier-mappings/variants/{variantId}
 * Fetch variant details with its supplier mappings
 */
export const getVariantSupplierDetails = async (variantId: string): Promise<TVariantSuppliersResponse> => {
  const { data } = await axiosDZAuthInstance.get<TVariantSuppliersResponse>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/variants/${variantId}`,
  )
  return data
}

/**
 * GET /dashboard/supplier-mappings/suppliers/{supplierCode}
 * Fetch supplier details with its variant mappings
 */
export const getSupplierVariantDetails = async (supplierCode: string): Promise<TSupplierVariantsResponse> => {
  const { data } = await axiosDZAuthInstance.get<TSupplierVariantsResponse>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/suppliers/${encodeURIComponent(supplierCode)}`,
  )
  return data
}

/** Validation error from bulk validate-update */
export type TBulkValidationError = {
  row: number
  field: string
  message: string
  value?: unknown
  errorCode?: string
}

/** Response shape for bulk validate-update */
export type TBulkValidateUpdateResponse = {
  isValid: boolean
  totalCount: number
  validCount: number
  invalidCount: number
  validationErrors: TBulkValidationError[]
  message: string
}

/**
 * POST /dashboard/supplier-mappings/bulk/validate-update
 * Validate file before bulk update
 */
export const validateBulkUpdate = async (file: File): Promise<TBulkValidateUpdateResponse> => {
  const formData = new FormData()
  formData.append('file', file, file.name)

  const { data } = await axiosDZAuthInstance.post<TBulkValidateUpdateResponse>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/bulk/validate-update`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

/**
 * POST /dashboard/supplier-mappings/bulk/update
 * Apply bulk update after validation passes
 */
export const bulkUpdate = async (file: File): Promise<{ updated?: number; message?: string }> => {
  const formData = new FormData()
  formData.append('file', file, file.name)

  const { data } = await axiosDZAuthInstance.post<{ updated?: number; message?: string }>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/bulk/update`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

/**
 * POST /dashboard/supplier-mappings/bulk/validate-upload
 * Validate file before bulk upload
 */
export const validateBulkUpload = async (file: File): Promise<TBulkValidateUpdateResponse> => {
  const formData = new FormData()
  formData.append('file', file, file.name)

  const { data } = await axiosDZAuthInstance.post<TBulkValidateUpdateResponse>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/bulk/validate-upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

/**
 * POST /dashboard/supplier-mappings/bulk/upload
 * Apply bulk upload after validation passes
 */
export const bulkUpload = async (file: File): Promise<{ created?: number; message?: string }> => {
  const formData = new FormData()
  formData.append('file', file, file.name)

  const { data } = await axiosDZAuthInstance.post<{ created?: number; message?: string }>(
    `${DASHBOARD()}/${EResource.SupplierMappings}/bulk/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}
